import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Input, Alert } from "antd";

import {
  SearchOutlined,
  FilterOutlined,
  MenuOutlined,
} from "@ant-design/icons";

import {
  ArrowUpRight,
  ArrowDownLeft,
  ExternalLink,
  Copy,
  Check,
  Fuel,
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  RefreshCw,
} from "lucide-react";

import "../components/Activity.css";

// Your existing backend API
const API_URL = "https://baitcoinexplorer.bait-coin.com";
// Your new backend proxy for Polygon
const BACKEND_API_URL = "https://backend.bait-coin.com";

/* ============================================================
   CHAIN CONFIG
   ============================================================ */
const CHAINS = {
  bait: {
    key: "bait",
    name: "GoldChain",
    symbol: "BAIT",
    source: "bait-api",
    explorerTx: (hash) =>
      `https://baitcoinexplorer.bait-coin.com/#/transaction/${hash}`,
  },
  pol: {
    key: "pol",
    name: "Polygon",
    symbol: "POL",
    source: "polygon-api",
    apiUrl: `${BACKEND_API_URL}/api/polygon/transactions`,
    explorerTx: (hash) => `https://polygonscan.com/tx/${hash}`,
  },
};

const DEFAULT_CHAIN = "bait";

/* ============================================================
   CHAIN DETECTION
   ============================================================ */
const resolveChainKey = (selectedChain) => {
  try {
    if (selectedChain) {
      const s = String(selectedChain).toLowerCase().trim();
      if (s === "0x89" || s === "137" || s.includes("pol") || s.includes("polygon") || s.includes("matic")) return "pol";
      if (s === "0x777771" || s === "777771" || s === "0xbdd5b" || s.includes("bait") || s.includes("gold")) return "bait";
    }

    const CHAIN_STORAGE_KEYS = ["chain", "selectedChain", "network", "chainName", "chainId"];
    const raw = CHAIN_STORAGE_KEYS.map((k) => localStorage.getItem(k)).filter(Boolean).join(" ").toLowerCase().trim();
    if (raw) {
      if (raw.includes("0x89") || raw.includes("137") || raw.includes("pol") || raw.includes("polygon") || raw.includes("matic")) return "pol";
      if (raw.includes("0x777771") || raw.includes("777771") || raw.includes("bait") || raw.includes("gold")) return "bait";
    }

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      const value = localStorage.getItem(key);
      if (!value) continue;
      const combined = `${key} ${value}`.toLowerCase();
      if (combined.includes("0x89") || combined.includes("137") || combined.includes("pol") || combined.includes("polygon") || combined.includes("matic")) return "pol";
      if (combined.includes("0x777771") || combined.includes("777771") || combined.includes("bait") || combined.includes("gold")) return "bait";
    }
    return DEFAULT_CHAIN;
  } catch (e) { return DEFAULT_CHAIN; }
};

/* ============================================================
   AMOUNT FORMATTER
   ============================================================ */
const formatAmount = (val) => {
  const n = Number(val);
  if (!Number.isFinite(n)) return "0";
  if (n === 0) return "0";
  const fixed = n.toFixed(6);
  const trimmed = fixed.replace(/0+$/, "").replace(/\.$/, "");
  return trimmed || "0";
};

/* ============================================================
   NORMALIZERS
   ============================================================ */
const normalizeBaitTx = (tx, walletAddress) => {
  const addr = (walletAddress || "").toLowerCase();
  const from = (tx.from || "").toLowerCase();
  const to = (tx.to || "").toLowerCase();
  let txType = tx.txType || (from !== addr && to === addr ? "Received" : "Sent");
  return { ...tx, txType, amount: Number(tx.valueBAIT ?? 0) };
};

const normalizePolygonTx = (tx, walletAddress) => {
  const addr = (walletAddress || "").toLowerCase();
  const from = tx.from || "";
  const to = tx.to || "";
  const fromL = from.toLowerCase();
  const toL = to.toLowerCase();

  let txType = "Sent";
  if (fromL !== addr && toL === addr) txType = "Received";

  const gasUsed = Number(tx.gasUsed || 0);
  const gasPrice = Number(tx.gasPrice || 0);
  const value = Number(tx.value || 0) / 1e18; // wei → POL

  const success = String(tx.isError) === "0" && (tx.txreceipt_status === undefined || String(tx.txreceipt_status) === "1");

  return {
    hash: tx.hash,
    from,
    to,
    blockNumber: tx.blockNumber,
    confirmations: tx.confirmations,
    status: success ? 1 : 0,
    txType,
    amount: value, // Native POL amount
    valueBAIT: value, // Keep legacy compatibility, but frontend uses `amount` now
    gasUsed,
    gasPrice,
    effectiveGasPrice: gasPrice,
    formattedTime: tx.timeStamp ? new Date(Number(tx.timeStamp) * 1000).toISOString() : null,
  };
};

/* ============================================================
   FETCHERS
   ============================================================ */
const fetchPolygonTransactions = async (address) => {
  if (!address) throw new Error("NO_WALLET_ADDRESS");

  // Call YOUR backend, not PolygonScan directly
  const { data } = await axios.get(CHAINS.pol.apiUrl, {
    params: { address },
    timeout: 15000,
  });

  if (!data?.success) {
    throw new Error(data?.message || "Failed to fetch Polygon transactions.");
  }

  if (!Array.isArray(data.transactions)) return [];

  return data.transactions
    .map((tx) => normalizePolygonTx(tx, address))
    .filter((tx) => tx.amount > 0); // Hide 0-value contract calls
};

const ArrowUpDownIcon = () => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
    <ArrowUpRight size={16} />
    <ArrowDownLeft size={16} style={{ marginLeft: -6 }} />
  </div>
);

/* ============================================================
   COMPONENT
   ============================================================ */
const Activity = ({ selectedChain }) => {
  const navigate = useNavigate();

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [walletAddress, setWalletAddress] = useState("");
  const [copiedHash, setCopiedHash] = useState("");
  const [expandedTxHash, setExpandedTxHash] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [chainKey, setChainKey] = useState(() => resolveChainKey(selectedChain));
  const [error, setError] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);

  const chain = CHAINS[chainKey] || CHAINS[DEFAULT_CHAIN];
  const pageSize = 5;

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const address = localStorage.getItem("address") || localStorage.getItem("walletAddress");
      const activeChainKey = resolveChainKey(selectedChain);
      const activeChain = CHAINS[activeChainKey] || CHAINS[DEFAULT_CHAIN];

      setChainKey(activeChainKey);

      if (!address) {
        setTransactions([]);
        setError("No wallet address found. Please connect your wallet.");
        setLoading(false);
        return;
      }
      setWalletAddress(address);

      let list = [];

      if (activeChain.source === "polygon-api") {
        list = await fetchPolygonTransactions(address);
      } else {
        const response = await axios.get(`${API_URL}/api/address/${address}/transactions`);
        if (response.data.success && Array.isArray(response.data.transactions)) {
          list = response.data.transactions.map((tx) => normalizeBaitTx(tx, address));
        }
      }

      setTransactions(list);
      setError("");
    } catch (err) {
      console.error("Activity Error:", err);
      setTransactions([]);
      if (err.message === "NO_WALLET_ADDRESS") {
        setError("No wallet address found.");
      } else {
        setError(err.message || "Could not load activity right now.");
      }
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [selectedChain]);

  useEffect(() => {
    fetchTransactions();
    const interval = setInterval(fetchTransactions, 15000);
    return () => clearInterval(interval);
  }, [fetchTransactions]);

  useEffect(() => {
    setCurrentPage(1);
    setExpandedTxHash(null);
  }, [chainKey]);

  /* HELPERS */
  const handleCopy = (text, type) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedHash(type);
    setTimeout(() => setCopiedHash(""), 2000);
  };

  const toggleTransactionDetails = (tx) => setExpandedTxHash(expandedTxHash === tx.hash ? null : tx.hash);
  const shortAddress = (addr) => addr ? `${addr.substring(0, 6)}...${addr.substring(addr.length - 4)}` : "-";
  const formatDate = (d) => d ? new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
  const getTxFee = (tx) => (Number(tx.gasUsed || 0) * Number(tx.effectiveGasPrice || tx.gasPrice || 0)) / 1e18;

  const stats = useMemo(() => {
    const totalTransactions = transactions.length;
    const received = transactions.filter((tx) => tx.txType === "Received").reduce((sum, tx) => sum + Number(tx.amount || 0), 0);
    const sent = transactions.filter((tx) => tx.txType !== "Received").reduce((sum, tx) => sum + Number(tx.amount || 0), 0);
    const gas = transactions.reduce((sum, tx) => sum + getTxFee(tx), 0);
    return { totalTransactions, received, sent, gas };
  }, [transactions]);

  const filteredTransactions = transactions.filter((tx) => {
    const search = searchText.toLowerCase();
    const address = `${tx.from} ${tx.to} ${tx.hash}`.toLowerCase();
    if (filterType === "received" && tx.txType !== "Received") return false;
    if (filterType === "sent" && tx.txType === "Received") return false;
    if (search && !address.includes(search)) return false;
    return true;
  });

  const paginatedTransactions = filteredTransactions.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const totalPages = Math.ceil(filteredTransactions.length / pageSize);

  useEffect(() => { if (totalPages > 0 && currentPage > totalPages) setCurrentPage(1); }, [totalPages, currentPage]);
  const handleRefresh = () => { setIsRefreshing(true); fetchTransactions(); };

  return (
    <div className="activity-page">
      <div className="activity-background"></div>
      <div className="activity-floating-menu"><button><MenuOutlined /></button></div>
      <div className="activity-wrapper">
        <div className="activity-hero">
          <div className="activity-hero-left">
            <button type="button" className="market-back-btn" onClick={() => navigate("/yourwallet")} title="Back to Wallet"><ArrowLeft size={18} /></button>
            <div><h1>ACTIVITY</h1><p>Your recent {chain.name} transactions</p></div>
          </div>
        </div>

        {/* STATS */}
        <div className="activity-stat-grid">
          <div className="activity-stat-card"><div className="stat-icon blue"><ArrowUpDownIcon /></div><div className="stat-title">Transactions</div><div className="stat-number">{stats.totalTransactions}</div><div className="stat-unit">All Time</div></div>
          <div className="activity-stat-card"><div className="stat-icon green"><ArrowDown size={18} /></div><div className="stat-title">Received</div><div className="stat-number green">{stats.received.toFixed(4)}</div><div className="stat-unit">{chain.symbol}</div></div>
          <div className="activity-stat-card"><div className="stat-icon purple"><ArrowUp size={18} /></div><div className="stat-title">Sent</div><div className="stat-number purple">{stats.sent.toFixed(4)}</div><div className="stat-unit">{chain.symbol}</div></div>
          <div className="activity-stat-card"><div className="stat-icon orange"><Fuel size={18} /></div><div className="stat-title">Gas Fee</div><div className="stat-number orange">{stats.gas.toFixed(6)}</div><div className="stat-unit">{chain.symbol}</div></div>
        </div>

        {/* TRANSACTIONS PANEL */}
        <div className="transactions-panel">
          <div className="transactions-header">
            <h2>Recent Transactions</h2>
            <div className="transactions-tools">
              <Input placeholder="Search" allowClear prefix={<SearchOutlined />} value={searchText} onChange={(e) => setSearchText(e.target.value)} />
              <button className="filter-btn" onClick={handleRefresh} disabled={isRefreshing} title="Refresh"><RefreshCw size={16} className={isRefreshing ? "spin" : ""} /></button>
              <button className="filter-btn"><FilterOutlined /> Filter</button>
            </div>
          </div>

          {loading && <div className="activity-loading"><div className="loading-spinner"></div><span>Loading transactions...</span></div>}

          {!loading && error && (
            <div className="activity-empty">
              <div className="empty-icon">⚠️</div>
              <h3>Unable to load activity</h3>
              <p>{error}</p>
              {chain.key === "pol" && (
                <a href={`https://polygonscan.com/address/${walletAddress}`} target="_blank" rel="noreferrer" className="view-explorer-link" style={{ display: "inline-flex", alignItems: "center", gap: "8px", background: "#8247e5", color: "#fff", padding: "10px 20px", borderRadius: "8px", textDecoration: "none", fontWeight: "bold", marginTop: "16px" }}>
                  <ExternalLink size={16} /> View on PolygonScan
                </a>
              )}
            </div>
          )}

          {!loading && !error && transactions.length === 0 && (
            <div className="activity-empty"><div className="empty-icon">📭</div><h3>No Activity Yet</h3><p>Your wallet has no {chain.name} transactions.</p></div>
          )}

          {!loading && !error && transactions.length > 0 && (
            <>
              <div className="activity-list">
                {paginatedTransactions.map((tx, index) => {
                  const isExpanded = expandedTxHash === tx.hash;
                  const txFee = getTxFee(tx);
                  return (
                    <React.Fragment key={tx.hash || index}>
                      <div className={`tx-card ${isExpanded ? "expanded" : ""}`} onClick={() => toggleTransactionDetails(tx)}>
                        <div className="tx-left">
                          <div className={`tx-icon ${tx.txType === "Received" ? "received" : "sent"}`}>
                            {tx.txType === "Received" ? <ArrowDownLeft size={18} /> : <ArrowUpRight size={18} />}
                          </div>
                          <div className="tx-details">
                            <div className="tx-title">{tx.txType === "Received" ? "Received" : "Sent"}</div>
                            <div className="tx-address">{tx.txType === "Received" ? shortAddress(tx.from) : shortAddress(tx.to)}</div>
                          </div>
                        </div>
                        <div className="tx-middle">
                          <div className="tx-date">{formatDate(tx.formattedTime)}</div>
                          <div className="tx-network">{chain.name}</div>
                        </div>
                        <div className="tx-right">
                          <div className={`tx-amount ${tx.txType === "Received" ? "green" : "purple"}`}>{tx.txType === "Received" ? "+" : "-"}{formatAmount(tx.amount)}</div>
                          <div className={`tx-status ${tx.status === 1 ? "confirmed" : "failed"}`}>{tx.status === 1 ? "Confirmed" : "Failed"}</div>
                        </div>
                        <div className={`tx-arrow ${isExpanded ? "open" : ""}`}>›</div>
                      </div>

                      {isExpanded && (
                        <div className="tx-inline-details">
                          <div className="tx-detail-body">
                            <div className="detail-row"><span className="detail-label">Status</span><span className={`status-badge ${tx.status === 1 ? "success" : "failed"}`}>{tx.status === 1 ? "Confirmed" : "Failed"}</span></div>
                            <div className="detail-row"><span className="detail-label">Date</span><span className="detail-value">{formatDate(tx.formattedTime)}</span></div>
                            <div className="detail-row">
                              <span className="detail-label">From</span>
                              <div className="detail-value-with-copy"><span className="detail-address">{tx.from || "-"}</span><button className="copy-btn" onClick={() => handleCopy(tx.from, "from")}>{copiedHash === "from" ? <Check size={14} /> : <Copy size={14} />}</button></div>
                            </div>
                            <div className="detail-row">
                              <span className="detail-label">To</span>
                              <div className="detail-value-with-copy"><span className="detail-address">{tx.to || "-"}</span><button className="copy-btn" onClick={() => handleCopy(tx.to, "to")}>{copiedHash === "to" ? <Check size={14} /> : <Copy size={14} />}</button></div>
                            </div>
                            <div className="detail-row">
                              <span className="detail-label">Transaction Hash</span>
                              <div className="detail-value-with-copy"><span className="detail-hash">{tx.hash || "-"}</span><button className="copy-btn" onClick={() => handleCopy(tx.hash, "hash")}>{copiedHash === "hash" ? <Check size={14} /> : <Copy size={14} />}</button></div>
                            </div>
                            <div className="detail-row"><span className="detail-label">Block</span><span className="detail-value">{tx.blockNumber || "-"}</span></div>
                            <div className="detail-row"><span className="detail-label">Confirmations</span><span className="detail-value">{tx.confirmations || "0"}</span></div>
                            <div className="detail-row"><span className="detail-label">Network</span><span className="detail-value">{chain.name}</span></div>
                            <div className="detail-row"><span className="detail-label">Network Fee</span><span className="detail-value">{formatAmount(txFee)} {chain.symbol}</span></div>
                            <div className="detail-row"><span className="detail-label">Total Amount</span><span className="detail-value">{formatAmount(tx.amount)} {chain.symbol}</span></div>
                          </div>
                          <div className="tx-detail-footer">
                            <a href={chain.explorerTx(tx.hash)} target="_blank" rel="noreferrer" className="view-explorer-link"><ExternalLink size={16} /> View on {chain.name === "Polygon" ? "PolygonScan" : "Explorer"}</a>
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>

              {/* PAGINATION */}
              <div className="activity-pagination">
                <button className="prev-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg> Previous
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  const showPage = page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                  if (showPage) return <button key={page} className={`page-number ${page === currentPage ? "active" : ""}`} onClick={() => setCurrentPage(page)}>{page}</button>;
                  if (page === 2 && currentPage > 3) return <span key="dots-start" className="page-dots">...</span>;
                  if (page === totalPages - 1 && currentPage < totalPages - 2) return <span key="dots-end" className="page-dots">...</span>;
                  return null;
                })}
                <button className="next-btn" disabled={currentPage === totalPages || totalPages === 0} onClick={() => setCurrentPage(currentPage + 1)}>
                  Next <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 6 15 12 9 18" /></svg>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Activity;